<template>
  <nav
    class="navbar is-fixed-top"
    :class="{
      'is-scrolled': isScrolled
    }"
    role="navigation"
    aria-label="main navigation"
  >
    <div class="container">
      <div class="navbar-brand">
        <router-link
          to="/"
          class="navbar-item"
        >
          <div class="logo">
            <img
              v-if="isDarkMode"
              src="/src/assets/images/logo-white.png"
              alt="Indiefy Logo"
              height="28"
            >
            <img
              v-else
              src="/src/assets/images/logo-dark.png"
              alt="Indiefy Logo"
              height="28"
            >
          </div>
        </router-link>

        <a
          role="button"
          class="navbar-burger burger"
          :class="{ 'is-active': isMenuOpen }"
          aria-label="menu"
          aria-expanded="false"
          @click="toggleMenu"
        >
          <span aria-hidden="true" />
          <span aria-hidden="true" />
          <span aria-hidden="true" />
        </a>
      </div>

      <div
        class="navbar-menu"
        :class="{ 'is-active': isMenuOpen }"
      >
        <div class="navbar-start">
          <router-link
            v-if="isAuthenticated"
            to="/dashboard"
            class="navbar-item nav-link-styled"
            :class="{ 'is-active': $route.path.startsWith('/dashboard') }"
          >
            <span class="nav-icon nav-icon--dashboard">
              <i class="fas fa-th-large" />
            </span>
            <span class="nav-text">{{ $t('navigation.dashboard') }}</span>
          </router-link>

          <router-link
            v-if="isAuthenticated"
            to="/distributions"
            class="navbar-item nav-link-styled"
            :class="{ 'is-active': $route.path.startsWith('/distributions') }"
          >
            <span class="nav-icon nav-icon--releases">
              <i class="fas fa-compact-disc" />
            </span>
            <span class="nav-text">{{ $t('navigation.releases') }}</span>
          </router-link>

          <router-link
            v-if="isAuthenticated"
            to="/analytics"
            class="navbar-item nav-link-styled"
            :class="{ 'is-active': $route.path.startsWith('/analytics') }"
          >
            <span class="nav-icon nav-icon--analytics">
              <i class="fas fa-chart-line" />
            </span>
            <span class="nav-text">{{ $t('navigation.analytics') }}</span>
          </router-link>

          <router-link
            v-if="isAuthenticated"
            to="/tools"
            class="navbar-item nav-link-styled"
            :class="{ 'is-active': $route.path.startsWith('/tools') }"
          >
            <span class="nav-icon nav-icon--tools">
              <i class="fas fa-wand-magic-sparkles" />
            </span>
            <span class="nav-text">{{ $t('navigation.tools') }}</span>
          </router-link>

          <router-link
            v-if="isAuthenticated"
            to="/feed"
            class="navbar-item nav-link-styled"
            :class="{ 'is-active': $route.path.startsWith('/feed') }"
          >
            <span class="nav-icon nav-icon--feed">
              <i class="fas fa-rss" />
            </span>
            <span class="nav-text">{{ $t('navigation.feed') }}</span>
          </router-link>

          <router-link
            v-if="isAuthenticated"
            to="/artists-catalog"
            class="navbar-item nav-link-styled"
            :class="{ 'is-active': $route.path.startsWith('/artists-catalog') }"
          >
            <span class="nav-icon nav-icon--artists">
              <i class="fas fa-users" />
            </span>
            <span class="nav-text">{{ $t('navigation.artists') }}</span>
          </router-link>
        </div>

        <div class="navbar-end">
          <div class="navbar-item action-buttons">
            <!-- Upgrade button for authenticated free users -->
            <router-link
              v-if="isAuthenticated && !isPremiumUser"
              to="/upgrade?from=v3_navbar"
              class="upgrade-button"
              :class="{ 'is-active': $route.path === '/upgrade?from=v3_navbar' }"
            >
              <i class="fas fa-crown" />
              <span>{{ $t('settings.upgrade') }}</span>
            </router-link>

            <!-- Notification inbox -->
            <NotificationInbox v-if="isAuthenticated" />

            <!-- Theme toggle button -->
            <button
              v-if="isPremiumUser"
              class="theme-toggle"
              @click="toggleDarkMode"
            >
              <i :class="isDarkMode ? 'fas fa-sun' : 'fas fa-moon'" />
            </button>

            <!-- Authentication buttons -->
            <template v-if="!isAuthenticated">
              <router-link
                to="/auth/login"
                class="auth-button login-button"
              >
                {{ $t('auth.sign-in') }}
              </router-link>

              <router-link
                to="/auth/signup"
                class="auth-button signup-button"
              >
                {{ $t('auth.sign-up') }}
              </router-link>
            </template>
          </div>

          <div
            class="navbar-item has-dropdown"
            :class="{ 'is-active': isLanguageDropdownOpen }"
          >
            <a
              class="navbar-link nav-link-styled"
              @click="toggleLanguageDropdown"
            >
              <span class="nav-icon nav-icon--language">
                <i class="fas fa-globe" />
              </span>
              <span class="nav-text current-lang">{{ currentLanguageDisplay }}</span>
            </a>

            <div class="navbar-dropdown is-right">
              <a
                v-for="language in availableLanguages"
                :key="language.code"
                class="navbar-item"
                :class="{ 'is-active': currentLanguage === language.code }"
                @click="changeLanguage(language.code)"
              >
                {{ language.name }}
              </a>
            </div>
          </div>

          <div
            v-if="isAuthenticated"
            class="navbar-item has-dropdown"
            :class="{ 'is-active': isAccountDropdownOpen }"
          >
            <a
              class="navbar-link nav-link-styled"
              @click="toggleAccountDropdown"
            >
              <span class="nav-icon nav-icon--account">
                <i class="fas fa-user-circle" />
              </span>
              <span class="nav-text">{{ $t('navigation.account') }}</span>
            </a>

            <div class="navbar-dropdown is-right">
              <!-- <a
                href="https://indiefy.net/dashboard?classic=1"
                class="navbar-item"
              >
                <i class="fa fa-arrow-right mr-3" /> Indiefy Classic
              </a> -->

              <a
                v-if="currentUser && currentUser.impersonate"
                class="navbar-item"
                href="https://indiefy.net/v1/auth/back-to-admin"
              >
                <i class="fa fa-arrow-left mr-3" /> Back to Admin
              </a>

              <router-link
                v-if="isAuthenticated"
                to="/royalties"
                class="navbar-item"
                :class="{ 'is-active': $route.path.startsWith('/royalties') }"
              >
                <i class="fa fa-money-bill-trend-up mr-3" />{{ $t('navigation.royalties') }}
              </router-link>

              <router-link
                to="/artists"
                class="navbar-item"
                @click="closeDropdowns"
              >
                <i class="fa fa-users mr-3" />{{ $t('navigation.artists') }}
              </router-link>

              <router-link
                to="/form-submissions"
                class="navbar-item"
                @click="closeDropdowns"
              >
                <i class="fa fa-file-text mr-3" />{{ $t('navigation.formSubmissions') }}
              </router-link>

              <router-link
                to="/account"
                class="navbar-item"
                @click="closeDropdowns"
              >
                <i class="fa fa-gear mr-3" />{{ $t('navigation.settings') }}
              </router-link>
              <hr class="navbar-divider">
              <a
                class="navbar-item"
                @click="logout"
              >
                <i class="fa fa-sign-out-alt mr-3" />{{ $t('auth.sign-out') }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useThemeStore } from '@/store/theme';
import { useAuthStore } from '@/store/auth';
import { getAvailableLanguages, setLanguage } from '@/i18n';
import { useI18n } from 'vue-i18n';
import NotificationInbox from '@/components/layout/NotificationInbox.vue';

// Router
const router = useRouter();

// Auth store
const authStore = useAuthStore();
const isAuthenticated = computed(() => authStore.isAuthenticated);
const currentUser = computed(() => authStore.currentUser);
const isPremiumUser = computed(() => authStore.currentUser && authStore.currentUser.subscription);

// Theme store
const themeStore = useThemeStore();
const isDarkMode = computed(() => themeStore.isDark);

// Menu state
const isMenuOpen = ref(false);
const isScrolled = ref(false);
const isLanguageDropdownOpen = ref(false);
const isAccountDropdownOpen = ref(false);

// i18n
const { locale } = useI18n();
const currentLanguage = computed(() => locale.value);
const availableLanguages = getAvailableLanguages();
const currentLanguageDisplay = computed(() => {
  const lang = availableLanguages.find(lang => lang.code === currentLanguage.value);
  return lang ? lang.name : 'EN';
});

// Methods
const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
  // Close dropdowns when toggling menu
  if (!isMenuOpen.value) {
    closeDropdowns();
  }
};

const toggleLanguageDropdown = () => {
  isLanguageDropdownOpen.value = !isLanguageDropdownOpen.value;
  // Close other dropdowns
  if (isLanguageDropdownOpen.value) {
    isAccountDropdownOpen.value = false;
  }
};

const toggleAccountDropdown = () => {
  isAccountDropdownOpen.value = !isAccountDropdownOpen.value;
  // Close other dropdowns
  if (isAccountDropdownOpen.value) {
    isLanguageDropdownOpen.value = false;
  }
};

const closeDropdowns = () => {
  isLanguageDropdownOpen.value = false;
  isAccountDropdownOpen.value = false;
};

const toggleDarkMode = () => {
  themeStore.toggleDarkMode();
};

const changeLanguage = (lang) => {
  setLanguage(lang);
  closeDropdowns();
};

const logout = async () => {
  await authStore.logout();
  router.push('/');
};

// Scroll handler
const handleScroll = () => {
  isScrolled.value = window.scrollY > 20;
};

// Close dropdowns when clicking outside
const handleClickOutside = (event) => {
  const dropdowns = document.querySelectorAll('.navbar-item.has-dropdown');
  let clickedInside = false;

  dropdowns.forEach(dropdown => {
    if (dropdown.contains(event.target)) {
      clickedInside = true;
    }
  });

  if (!clickedInside) {
    closeDropdowns();
  }
};

// Lifecycle hooks
onMounted(() => {
  window.addEventListener('scroll', handleScroll);
  document.addEventListener('click', handleClickOutside);
  handleScroll(); // Initial check
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped lang="scss">
@import "@/styles/abstracts/colors";
@import "@/styles/abstracts/animations";
@import "@/styles/abstracts/breakpoints";

.navbar {
  transition: $transition-base;
  padding: 0.75rem 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--color-border, rgba(0, 0, 0, 0.1));
  z-index: 30;

  .logo {
    max-height: 28px;
    transition: $transition-base;
    display: flex;
    align-items: center;

    img {
      max-height: 28px;
      transition: transform 0.3s ease;
    }
  }

  &.is-scrolled {
    padding: 0.4rem 0;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

    .logo {
      img {
        transform: scale(0.9);
      }
    }
  }

  .navbar-menu {
    @media screen and (max-width: $tablet) {
      position: absolute;
      width: 100%;
      left: 0;
      right: 0;
      top: 100%;
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
      border-bottom: 1px solid var(--color-border, rgba(0, 0, 0, 0.1));
      background-color: var(--color-card-background, white);
      padding: 0.5rem 0;

      .body--dark & {
        background-color: var(--color-card-background, #1e293b);
        border-color: rgba(255, 255, 255, 0.1);
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
      }
    }
  }

  // Styled navigation links with icons
  .nav-link-styled {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    border-radius: 8px;
    transition: all 0.25s ease;
    position: relative;

    .nav-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border-radius: 6px;
      font-size: 0.85rem;
      transition: all 0.25s ease;

      // Desktop: hide icons by default for main nav items (not dropdowns)
      @media screen and (min-width: $tablet + 1) {
        opacity: 0;
        width: 0;
        margin-right: -0.5rem;
      }

      // Color variants for each nav icon
      &--dashboard {
        background: linear-gradient(135deg, rgba($indiefy-blue, 0.15), rgba($indiefy-lavender, 0.1));
        color: $indiefy-blue;

        .body--dark & {
          background: linear-gradient(135deg, rgba($indiefy-blue, 0.25), rgba($indiefy-lavender, 0.15));
        }
      }

      &--releases {
        background: linear-gradient(135deg, rgba($indiefy-lavender, 0.15), rgba($indiefy-pink, 0.1));
        color: $indiefy-lavender;

        .body--dark & {
          background: linear-gradient(135deg, rgba($indiefy-lavender, 0.25), rgba($indiefy-pink, 0.15));
        }
      }

      &--analytics {
        background: linear-gradient(135deg, rgba($indiefy-teal, 0.15), rgba($indiefy-blue, 0.1));
        color: $indiefy-teal;

        .body--dark & {
          background: linear-gradient(135deg, rgba($indiefy-teal, 0.25), rgba($indiefy-blue, 0.15));
        }
      }

      &--tools {
        background: linear-gradient(135deg, rgba($indiefy-orange, 0.15), rgba($indiefy-gold, 0.1));
        color: $indiefy-orange;

        .body--dark & {
          background: linear-gradient(135deg, rgba($indiefy-orange, 0.25), rgba($indiefy-gold, 0.15));
        }
      }

      &--feed {
        background: linear-gradient(135deg, rgba($indiefy-pink, 0.15), rgba($indiefy-lavender, 0.1));
        color: $indiefy-pink;

        .body--dark & {
          background: linear-gradient(135deg, rgba($indiefy-pink, 0.25), rgba($indiefy-lavender, 0.15));
        }
      }

      &--artists {
        background: linear-gradient(135deg, rgba($indiefy-dark-purple, 0.15), rgba($indiefy-blue, 0.1));
        color: $indiefy-dark-purple;

        .body--dark & {
          background: linear-gradient(135deg, rgba($indiefy-lavender, 0.25), rgba($indiefy-blue, 0.15));
          color: $indiefy-lavender;
        }
      }

      &--language {
        background: linear-gradient(135deg, rgba($indiefy-teal, 0.15), rgba($indiefy-blue, 0.1));
        color: $indiefy-teal;

        .body--dark & {
          background: linear-gradient(135deg, rgba($indiefy-teal, 0.25), rgba($indiefy-blue, 0.15));
        }
      }

      &--account {
        background: linear-gradient(135deg, rgba($indiefy-blue, 0.15), rgba($indiefy-teal, 0.1));
        color: $indiefy-blue;

        .body--dark & {
          background: linear-gradient(135deg, rgba($indiefy-blue, 0.25), rgba($indiefy-teal, 0.15));
        }
      }
    }

    .nav-text {
      font-weight: 500;
      font-size: 0.95rem;
    }

    &:hover {
      background-color: var(--color-background-alt, rgba(0, 0, 0, 0.03));

      .nav-icon {
        transform: scale(1.1);

        // Desktop: show icons on hover
        @media screen and (min-width: $tablet + 1) {
          opacity: 1;
          width: 28px;
          margin-right: 0;
        }
      }
    }

    &.is-active {
      background-color: rgba($indiefy-blue, 0.08);

      .body--dark & {
        background-color: rgba($indiefy-blue, 0.15);
      }

      .nav-text {
        font-weight: 600;
        color: $indiefy-blue;
      }

      .nav-icon {
        transform: scale(1.05);

        // Desktop: show icons when active
        @media screen and (min-width: $tablet + 1) {
          opacity: 1;
          width: 28px;
          margin-right: 0;
        }

        &--dashboard {
          background: linear-gradient(135deg, $indiefy-blue, $indiefy-lavender);
          color: white;
        }

        &--releases {
          background: linear-gradient(135deg, $indiefy-lavender, $indiefy-pink);
          color: white;
        }

        &--analytics {
          background: linear-gradient(135deg, $indiefy-teal, $indiefy-blue);
          color: white;
        }

        &--tools {
          background: linear-gradient(135deg, $indiefy-orange, $indiefy-gold);
          color: white;
        }

        &--feed {
          background: linear-gradient(135deg, $indiefy-pink, $indiefy-lavender);
          color: white;
        }

        &--artists {
          background: linear-gradient(135deg, $indiefy-dark-purple, $indiefy-blue);
          color: white;
        }
      }
    }

    // Mobile styling - always show icons
    @media screen and (max-width: $tablet) {
      padding: 0.75rem 1rem;
      margin: 0.25rem 0.75rem;
      border-radius: 10px;

      .nav-icon {
        opacity: 1 !important;
        width: 36px !important;
        height: 36px;
        border-radius: 8px;
        font-size: 1rem;
        margin-right: 0 !important;
      }

      .nav-text {
        font-size: 1rem;
      }

      &:hover, &:active {
        background-color: var(--color-background-alt, rgba(0, 0, 0, 0.05));
      }
    }
  }

  .navbar-item {
    font-weight: 500;
    transition: all 0.2s ease;
    color: var(--color-text, #333);

    .body--dark & {
      color: var(--color-text, #e2e8f0);
    }

    &:hover, &.is-active {
      color: var(--color-primary, $indiefy-blue);
    }

    &.is-active {
      font-weight: 600;
    }

    &.has-dropdown {
      .navbar-link {
        background-color: transparent;
        color: var(--color-text, #333);
        cursor: pointer;
        padding-right: 2rem; // Space for dropdown arrow

        .body--dark & {
          color: var(--color-text, #e2e8f0);
        }

        // Dropdown arrow with proper spacing
        &:after {
          transition: transform 0.2s ease;
          border-color: var(--color-text-secondary, #666);
          margin-left: 0.5rem;
          right: 0.75rem;
        }

        // When it's a styled nav link, adjust spacing
        &.nav-link-styled {
          padding-right: 2.25rem;

          .nav-text {
            margin-right: 0.25rem;
          }
        }
      }

      &.is-active .navbar-link:after {
        transform: rotate(135deg);
      }

      &:hover .navbar-link {
        background-color: transparent;
      }

      .navbar-dropdown {
        background-color: var(--color-card-background, white);
        border-color: var(--color-border);
        border-radius: 10px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
        display: none;
        overflow: hidden;

        .body--dark & {
          background-color: var(--color-card-background, #1e293b);
          border-color: rgba(255, 255, 255, 0.1);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
        }

        .navbar-item {
          padding: 0.75rem 1rem;
          color: var(--color-text, #333);
          transition: all 0.2s ease;

          .body--dark & {
            color: var(--color-text, #e2e8f0);
          }

          i {
            width: 24px;
            color: $indiefy-blue;
          }

          &:hover {
            background-color: rgba($indiefy-blue, 0.08);
            color: $indiefy-blue;

            .body--dark & {
              background-color: rgba($indiefy-blue, 0.15);
            }
          }
        }

        .navbar-divider {
          background-color: var(--color-border, rgba(0, 0, 0, 0.1));
          margin: 0.5rem 0;

          .body--dark & {
            background-color: rgba(255, 255, 255, 0.1);
          }
        }
      }

      &.is-active .navbar-dropdown {
        display: block;
      }

      // Mobile dropdown styling
      @media screen and (max-width: $tablet) {
        .navbar-link.nav-link-styled {
          margin: 0.25rem 0.75rem;
        }
      }
    }
  }

  // Action buttons styling
  .action-buttons {
    display: flex;
    align-items: center;
    gap: 0.75rem;

    @media screen and (max-width: $tablet) {
      padding: 0.5rem 1rem;
      border-top: 1px solid var(--color-border, rgba(0, 0, 0, 0.1));
      margin-top: 0.5rem;

      .body--dark & {
        border-color: rgba(255, 255, 255, 0.1);
      }
    }

    // Theme toggle button
    .theme-toggle {
      background: linear-gradient(135deg, rgba($indiefy-lavender, 0.15), rgba($indiefy-blue, 0.1));
      border: none;
      color: $indiefy-lavender;
      font-size: 1.1rem;
      padding: 0.4rem;
      border-radius: 8px;
      height: 36px;
      width: 36px;
      cursor: pointer;
      transition: all 0.25s ease;
      display: flex;
      align-items: center;
      justify-content: center;

      .body--dark & {
        background: linear-gradient(135deg, rgba($indiefy-gold, 0.2), rgba($indiefy-orange, 0.15));
        color: $indiefy-gold;
      }

      &:hover {
        transform: scale(1.1) rotate(15deg);
        background: linear-gradient(135deg, $indiefy-lavender, $indiefy-blue);
        color: white;

        .body--dark & {
          background: linear-gradient(135deg, $indiefy-gold, $indiefy-orange);
        }
      }
    }

    // Upgrade button
    .upgrade-button {
      margin-right: 0.5rem;
      color: white !important;
      background: linear-gradient(135deg, $indiefy-gold, $indiefy-orange);
      padding: 0.5rem 1rem;
      border-radius: 8px;
      font-weight: 600;
      font-size: 0.9rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      transition: all 0.25s ease;
      box-shadow: 0 2px 8px rgba($indiefy-orange, 0.3);

      i {
        font-size: 0.85rem;
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 16px rgba($indiefy-orange, 0.4);
      }
    }

    // Auth buttons
    .auth-button {
      border-radius: 8px;
      padding: 0.5rem 1.25rem;
      font-weight: 500;
      font-size: 0.9rem;
      transition: all 0.25s ease;

      &.login-button {
        background-color: transparent;
        color: var(--color-text);
        border: 1.5px solid var(--color-border);

        &:hover {
          background-color: var(--color-background-alt);
          border-color: $indiefy-blue;
          color: $indiefy-blue;
          transform: translateY(-2px);
        }
      }

      &.signup-button {
        background: linear-gradient(135deg, $indiefy-blue, $indiefy-lavender);
        color: white;
        border: none;
        font-weight: 600;
        box-shadow: 0 2px 8px rgba($indiefy-blue, 0.3);

        &:hover {
          box-shadow: 0 4px 16px rgba($indiefy-blue, 0.4);
          transform: translateY(-2px);
        }
      }
    }

    // New release button
    .new-release-button {
      background: linear-gradient(135deg, $indiefy-blue, $indiefy-lavender);
      color: white;
      border: none;
      font-weight: 600;
      border-radius: 8px;
      padding: 0.5rem 1.25rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      transition: all 0.25s ease;
      box-shadow: 0 2px 8px rgba($indiefy-blue, 0.3);

      i {
        font-size: 0.9rem;
      }

      &:hover {
        box-shadow: 0 4px 16px rgba($indiefy-blue, 0.4);
        transform: translateY(-2px);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }
}
</style>